import { useEffect, useState } from 'react';
import { listVersions, loadVersionContent, type VersionEntry } from '../data/versions.ts';
import { diffWords } from '../lib/diff.ts';
import { Icon } from './Icon.tsx';
import type { LocalNote } from '../data/types.ts';

type Props = {
  note: LocalNote;
  onClose: () => void;
  onRestore: (content: string) => void;
};

function formatStamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function History({ note, onClose, onRestore }: Props): JSX.Element {
  const [entries, setEntries] = useState<VersionEntry[] | null>(null);
  const [selected, setSelected] = useState<VersionEntry | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void listVersions(note.id).then(setEntries);
  }, [note.id]);

  useEffect(() => {
    if (!selected) {
      setContent(null);
      return;
    }
    setError(null);
    setContent(null);
    void loadVersionContent(note, selected.id)
      .then(setContent)
      .catch(() => setError('Dieser Stand liegt nur auf dem Server und ist gerade nicht erreichbar.'));
  }, [selected, note]);

  const parts = content === null ? [] : diffWords(content, note.content);
  const added = parts.filter((part) => part.type === 'added').reduce((sum, part) => sum + part.text.length, 0);
  const removed = parts.filter((part) => part.type === 'removed').reduce((sum, part) => sum + part.text.length, 0);

  return (
    <div className="panel">
      <header className="panel-head">
        <h2>Verlauf</h2>
        <button className="head-icon" onClick={onClose} title="Schließen">
          <Icon name="close" />
        </button>
      </header>

      <div className="panel-body">
        <ol className="versions">
          {entries === null && <li className="muted">wird geladen …</li>}
          {entries?.length === 0 && (
            <li className="muted">
              Noch keine Stände. Der erste entsteht nach fünf Minuten ohne Eingabe.
            </li>
          )}
          {entries?.map((entry) => (
            <li key={entry.id}>
              <button
                className={selected?.id === entry.id ? 'version version-on' : 'version'}
                onClick={() => setSelected(entry)}
              >
                <span>{formatStamp(entry.createdAt)}</span>
                <em className={entry.charDelta < 0 ? 'balance-loss' : 'balance-gain'}>
                  {entry.labeled && '◆ '}
                  {entry.charDelta > 0 ? `+${entry.charDelta}` : entry.charDelta}
                </em>
              </button>
            </li>
          ))}
        </ol>

        <div className="diff">
          {error && <p className="error">{error}</p>}
          {selected && content === null && !error && <p className="muted">wird entschlüsselt …</p>}
          {content !== null && (
            <>
              <p className="muted">
                <span className="balance-gain">+{added}</span>{' '}
                <span className="balance-loss">−{removed}</span> Zeichen gegenüber diesem Stand.
                Nur was schmerzt, wird warm.
              </p>
              <pre className="diff-body">
                {parts.map((part, index) => (
                  <span key={index} className={`diff-${part.type}`}>
                    {part.text}
                  </span>
                ))}
              </pre>
              <button
                className="solid"
                onClick={() => {
                  onRestore(content);
                  onClose();
                }}
              >
                Diesen Stand wiederherstellen
              </button>
              <p className="hint">
                Wiederherstellen überschreibt nichts: Der aktuelle Stand wird vorher als
                Version gesichert.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
